import React, { useCallback, useEffect, useRef, useState } from "react";
import { PlayerApi } from "./api";
import {
  detectDeviceMeta, humanLabel, platformString, DeviceMeta,
} from "./deviceMeta";
import {
  loadPendingPairing, savePendingPairing, clearPendingPairing,
} from "./storage";
import { IPlayerPairedDevice } from "./types";
import "./player.css";

const APP_VERSION = "web-1.0.0";
/** Default poll cadence; the server also returns poll_interval_seconds. */
const DEFAULT_POLL_MS = 5_000;

type PairPhase =
  | { kind: "starting" }
  | { kind: "waiting"; code: string }
  | { kind: "error" };

/**
 * Unregistered-device pairing screen for the bare `/player` route.
 *
 * Flow (all backed by endpoints that already exist — see
 * docs/player-api.md §"Device/TV pairing"):
 *   1. POST /device/pair/start  → show `pairing_code`, keep session_token
 *   2. poll POST /device/pair/poll every ~5s
 *   3. an admin claims the code in Devices → next poll returns `activated`
 *      with the device credential, handed up via `onPaired`.
 *
 * A pending (unclaimed) session is persisted so a reload keeps the SAME
 * on-screen code rather than confusing the installer with a new one. An
 * expired session silently rolls over to a fresh code.
 */
const Pairing: React.FC<{
  onPaired: (device: IPlayerPairedDevice, token: string) => void;
}> = ({ onPaired }) => {
  const [phase, setPhase] = useState<PairPhase>({ kind: "starting" });
  const [meta] = useState<DeviceMeta>(() => detectDeviceMeta());

  // Private session token + poll cadence live on refs so the single poll
  // interval never has to be re-created when they change.
  const sessionTokenRef = useRef<string | null>(null);
  const pollMsRef = useRef<number>(DEFAULT_POLL_MS);
  const onPairedRef = useRef(onPaired);
  useEffect(() => { onPairedRef.current = onPaired; }, [onPaired]);

  /** Request a brand-new pairing session (used on first load, on expiry,
   *  and to recover from a server-rejected session token). */
  const startSession = useCallback(async () => {
    setPhase({ kind: "starting" });
    const res = await PlayerApi.startPairing(APP_VERSION, platformString(meta));
    if (!res.ok) {
      // Network / throttle (429) / server error — retry shortly without
      // wiping the screen into an alarming error forever.
      setPhase({ kind: "error" });
      window.setTimeout(() => { void startSession(); }, 8_000);
      return;
    }
    sessionTokenRef.current = res.data.session_token;
    pollMsRef.current = Math.max(2, res.data.poll_interval_seconds || 5) * 1000;
    savePendingPairing({
      pairing_code: res.data.pairing_code,
      session_token: res.data.session_token,
      expires_at: res.data.expires_at,
    });
    setPhase({ kind: "waiting", code: res.data.pairing_code });
  }, [meta]);

  /** Resume a stored unexpired session if one exists, else start fresh. */
  useEffect(() => {
    const pending = loadPendingPairing();
    if (pending) {
      sessionTokenRef.current = pending.session_token;
      setPhase({ kind: "waiting", code: pending.pairing_code });
    } else {
      void startSession();
    }
  }, [startSession]);

  /* ─── Poll loop ─────────────────────────────────────────────────────── */

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const schedule = () => {
      if (cancelled) return;
      timer = window.setTimeout(tick, pollMsRef.current);
    };

    const tick = async () => {
      if (cancelled) return;
      const token = sessionTokenRef.current;
      if (!token) { schedule(); return; }

      const res = await PlayerApi.pollPairing(token);
      if (cancelled) return;

      if (!res.ok) {
        // 422 → the session token is unknown/expired server-side. Start a
        // fresh session (new code). Anything else (network) → just retry.
        if (res.status === 422) {
          clearPendingPairing();
          sessionTokenRef.current = null;
          await startSession();
        }
        schedule();
        return;
      }

      const data = res.data;
      if (data.status === "activated") {
        clearPendingPairing();
        onPairedRef.current(data.device, data.token);
        return; // stop polling — parent swaps us out for the player
      }
      if (data.status === "expired") {
        // The displayed code timed out before an admin claimed it. Roll
        // straight over to a fresh code so the installer always has a
        // live code to read.
        clearPendingPairing();
        sessionTokenRef.current = null;
        await startSession();
        schedule();
        return;
      }
      // pending / claimed / consumed → keep polling.
      schedule();
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [startSession]);

  /* ─── Render ────────────────────────────────────────────────────────── */

  const code = phase.kind === "waiting" ? phase.code : null;

  return (
    <div className="vmd-player vmd-player--pairing">
      <div className="vmd-pair-card">
        <div className="vmd-player-brand">VM DISPLAY</div>
        <div className="vmd-pair-heading">Connect this screen</div>

        {code ? (
          <div className="vmd-pair-code" aria-label={`Device code ${code}`}>
            {code}
          </div>
        ) : (
          <div className="vmd-pair-code" style={{ opacity: 0.5 }}>· · · · · ·</div>
        )}

        <div className="vmd-pair-instructions">
          In VM Display, open <strong>Devices → Add Device</strong>, choose
          <br /><strong>“Pair using code displayed on TV”</strong>, and enter this code.
        </div>

        <div className="vmd-pair-status">
          <span className="vmd-pair-spinner" />
          {phase.kind === "error"
            ? "Reconnecting…"
            : code
              ? "Waiting for approval"
              : "Preparing…"}
        </div>

        <div className="vmd-pair-hint">
          This screen: {humanLabel(meta)}
          {meta.pixelRatio && meta.pixelRatio !== 1 ? ` @${meta.pixelRatio}x` : ""}
        </div>
      </div>
    </div>
  );
};

export default Pairing;
